/**
 * When `window.myObject` is not defined and we try to recreate it dynamically we will not save it
 * to the original name but a random id. Why? Others could override the prototype of the window object
 * name and make it unusable (e.g. setting via `Object.defineProperty(window, "myObject", { value: undefined, writable: false, configurable: false })`).
 */ const fallbackSlugToKey = {};
/**
 * Get the anonymous localized script from the document as object and write it to the window object.
 *
 * Use it in combination with `Assets.php#anonymous_localize_script` or
 * `wpLocalizeScriptResources` from `@devowl-wp/deliver-anonymous-asset`.
 *
 * @example
 * ```ts
 * const options = getAnonymousLocalizedScript(process.env.slug);
 * ```
 */ function getAnonymousLocalizedScript(slug) {
    const windowObject = window[slug.replace(/-([a-z])/g, (g)=>g[1].toUpperCase())];
    const fallbackKey = fallbackSlugToKey[slug];
    if (!windowObject) {
        if (fallbackKey) {
            return window[fallbackKey];
        }
        // Case 1: window.myObject is not defined and probably overridden. But `anonymous_localize_script`
        // will set a random id and return the object via `window[randomId]`. We need to search and cache it.
        for(const key in window){
            // Do not trigger Google PageSpeed Insights warnings about deprecated APIs
            if ([
                "sharedStorage"
            ].includes(key)) {
                continue;
            }
            try {
                const value = window[key];
                if (value?.textDomain === slug) {
                    fallbackSlugToKey[slug] = key;
                    return value;
                }
            } catch (error) {
            // e.g. SecurityError: Failed to read a named property 'value' from 'Window': Blocked a frame with origin "..." from accessing a cross-origin frame.
            // We just ignore this error.
            }
        }
    }
    return windowObject;
}
export { getAnonymousLocalizedScript };
